import { BaseSyntheticEvent, useContext, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { useForm } from 'react-hook-form'
import { m, AnimatePresence, Variants } from 'framer-motion'

import { LanguageContext } from '@lib/language'
import { SiteContext } from '@lib/site'
import { StringsContext } from '@lib/strings'
import { WaitlistResponse } from '@pages/api/klaviyo/waitlist-join'

import Alert from '@components/alert'
import Button, { ButtonVariant } from '@components/buttons/button'
import InputField from '@components/input-field'

const formAnimation: Variants = {
  show: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: 'linear',
      when: 'beforeChildren',
    },
  },
  hide: {
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: 'linear',
      when: 'afterChildren',
    },
  },
}

interface ProductWaitlistProps {
  variantId: number
}

interface ProductWaitlistFormValues {
  fullname: string
  email: string
}

const ProductWaitlist = ({ variantId }: ProductWaitlistProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<ProductWaitlistFormValues>()
  const strings = useContext(StringsContext)
  const { locale } = useContext(LanguageContext)
  const { settings } = useContext(SiteContext)

  if (!settings?.klaviyoAccountID) {
    return (
      <Button
        disabled
        variant={ButtonVariant.SECONDARY}
        className="w-full sm:w-auto min-w-[240px]"
      >
        {strings.productOutOfStock}
      </Button>
    )
  }

  const resetForm = (event: BaseSyntheticEvent) => {
    event.preventDefault()

    reset()
    setIsError(false)
    setIsSuccess(false)
    setIsSubmitting(false)
  }

  const addToWaitlist = async (
    productWaitlistFormValues: ProductWaitlistFormValues
  ) => {
    const payload = JSON.stringify({
      variant: variantId,
      ...productWaitlistFormValues,
    })

    try {
      await axios.post<
        WaitlistResponse,
        AxiosResponse<WaitlistResponse>,
        string
      >('/api/klaviyo/waitlist-join', payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      })

      setIsSubmitting(false)
      setIsSuccess(true)
    } catch (error) {
      setIsSubmitting(false)
      setIsError(true)
      console.log(error)
    }
  }

  const onSubmit = (
    productWaitlistFormValues: ProductWaitlistFormValues,
    event?: BaseSyntheticEvent
  ) => {
    event?.preventDefault()

    if (!variantId) {
      setIsError(true)
      return
    }

    setIsSubmitting(true)
    setIsError(false)
    addToWaitlist(productWaitlistFormValues)
  }

  const fullnameRegister = register('fullname')
  const emailRegister = register('email', {
    required: strings.emailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })

  return (
    <div>
      <AnimatePresence exitBeforeEnter initial={false}>
        {!isError && !isSuccess && (
          <m.form
            key="form"
            initial="hide"
            animate="show"
            exit="hide"
            variants={formAnimation}
            className="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <input
              type="text"
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
              onChange={fullnameRegister.onChange}
              onBlur={fullnameRegister.onBlur}
              ref={fullnameRegister.ref}
              name={fullnameRegister.name}
            />

            <div className="flex flex-wrap items-center">
              <InputField
                type="email"
                formRegister={emailRegister}
                placeholder={strings.emailAddress}
                errorMessage={errors.email?.message}
                borderBottom
                className="mt-4 mr-4 min-w-full sm:min-w-[300px]"
              />

              <Button
                disabled={isSubmitting}
                variant={ButtonVariant.PRIMARY}
                className="mt-4"
              >
                {isSubmitting
                  ? strings.buttonWaiting
                  : strings.productWaitlistSubmit}
              </Button>
            </div>
          </m.form>
        )}

        {isSuccess && (
          <m.div
            key="success"
            initial="hide"
            animate="show"
            exit="hide"
            variants={formAnimation}
          >
            <Alert success onClick={resetForm}>
              <p>{strings.productWaitlistSuccess}</p>
            </Alert>
          </m.div>
        )}

        {isError && (
          <m.div
            key="error"
            initial="hide"
            animate="show"
            exit="hide"
            variants={formAnimation}
          >
            <Alert
              error
              buttonText={strings.buttonTryAgain}
              onClick={resetForm}
            >
              <p>{strings.productWaitlistError}</p>
            </Alert>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProductWaitlist
